import { useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button, Input } from '@nsibi/ui-web';
import { useTranslation } from 'react-i18next';

import { verifyLockPin, type LockIdentity } from '../lib/auth';
import { PIN_LENGTH, isValidPin, readPinFailure, waitParts } from '../lib/emailAuth';
import type { Session } from '../lib/session';
import { useCountdown } from '../lib/useCountdown';

/**
 * The registration-lock PIN, after an emailed code or a Google sign-in
 * proved who the person is (`POST /v1/auth/phone/lock/verify/`).
 *
 * A wrong PIN is not a wrong code: it never sends anybody back for another
 * email or another Google sheet - the proof is already spent and the PIN has
 * its own ladder (`retryAfter`). Only a lapsed challenge goes back to the
 * start (`onExpired`).
 */
export function PinStep({
  identity,
  challenge,
  retryAfter,
  onSignedIn,
  onBack,
  onExpired,
  backLabel,
}: {
  identity: LockIdentity;
  challenge: string;
  /** The lock's own cool-down, from the answer that asked for the PIN. */
  retryAfter: number;
  onSignedIn: (s: Session) => void;
  onBack: () => void;
  /** The challenge lapsed or was spent: the proof behind it is gone. */
  onExpired: () => void;
  backLabel: string;
}) {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pinWait = useCountdown();

  useEffect(() => {
    pinWait.start(retryAfter);
    // Once, for the challenge this step was opened with.
  }, [challenge]);

  const waitText = (seconds: number) => {
    const { unit, n } = waitParts(seconds);
    return unit === 'h'
      ? t('auth.email.tryAgainInH', { n, defaultValue: 'Try again in {{n}} h' })
      : unit === 'min'
        ? t('auth.email.tryAgainInMin', { n, defaultValue: 'Try again in {{n}} min' })
        : t('auth.email.tryAgainInS', { n, defaultValue: 'Try again in {{n}}s' });
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || pinWait.left > 0 || !isValidPin(pin)) return;
    setBusy(true);
    setError(null);
    try {
      const { session } = await verifyLockPin(identity, challenge, pin);
      onSignedIn(session);
    } catch (err) {
      const failure = readPinFailure(err);
      setPin('');
      switch (failure.kind) {
        case 'wrong':
          pinWait.start(failure.retryAfter);
          setError(t('auth.thatPinIsNotRight', { defaultValue: 'That PIN is not right.' }));
          break;
        case 'throttled':
          pinWait.start(failure.retryAfter || 60);
          setError(
            t('auth.email.tooMany', {
              defaultValue: 'Too many attempts. Wait a little before trying again.',
            }),
          );
          break;
        case 'expired':
          onExpired();
          break;
        case 'app_check':
          setError(
            t('auth.web.appCheckFailed', {
              defaultValue:
                "This browser can't sign in right now. Use the Nsibi app, or try again later.",
            }),
          );
          break;
        case 'network':
          setError(
            t('auth.email.network', {
              defaultValue: "Can't reach the server. Check your connection and try again.",
            }),
          );
          break;
        default:
          setError(t('auth.somethingWentWrongTry', { defaultValue: 'Something went wrong. Try again.' }));
      }
    } finally {
      setBusy(false);
    }
  }

  const locked = pinWait.left > 0;
  return (
    <form onSubmit={submit}>
      <h1 className="text-display">
        {t('auth.twoStepVerificationPin', { defaultValue: 'Two-step verification PIN' })}
      </h1>
      <p className="mt-3 text-muted-foreground">
        {t('auth.email.thisAccountHasTwoStep', {
          pinLength: PIN_LENGTH,
          defaultValue:
            'This account has two-step verification turned on. Enter your {{pinLength}}-digit PIN.',
        })}
      </p>

      <label htmlFor="pin" className="mt-8 block text-sm font-medium">
        {t('auth.enterYourPin', { defaultValue: 'Enter your PIN' })}
      </label>
      <Input
        id="pin"
        type="password"
        inputMode="numeric"
        autoComplete="off"
        autoFocus
        maxLength={PIN_LENGTH}
        value={pin}
        onChange={(e) => {
          setPin(e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH));
          if (error) setError(null);
        }}
        className="mt-2 h-11 font-mono tracking-[0.3em]"
      />

      {error && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {error}
        </p>
      )}

      <Button
        type="submit"
        size="lg"
        disabled={busy || locked || !isValidPin(pin)}
        className="mt-6 w-full"
      >
        {busy ? <Loader2 className="animate-spin" /> : null}
        {busy ? t('components.checking') : locked ? waitText(pinWait.left) : t('components.signIn')}
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mt-3 w-full"
        disabled={busy}
        onClick={onBack}
      >
        <ArrowLeft />
        {backLabel}
      </Button>
    </form>
  );
}
